import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay, tap, throwError } from 'rxjs';
import { AuthResponse, UserProfile } from '../models';

/**
 * Sessione: l'access token JWT vive SOLO in memoria (mai in localStorage: non e' leggibile da uno script
 * iniettato); il refresh token e' un cookie HttpOnly gestito dal browser. Al reload si recupera la sessione
 * con un refresh silenzioso.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  private readonly _user = signal<UserProfile | null>(null);
  private readonly _token = signal<string | null>(null);
  private refreshInFlight$: Observable<string> | null = null;

  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => this._user() !== null);
  private readonly permissionSet = computed(() => new Set(this._user()?.permissions ?? []));

  accessToken(): string | null {
    return this._token();
  }

  has(permission: string): boolean {
    return this.permissionSet().has(permission);
  }

  hasAny(...permissions: string[]): boolean {
    return permissions.some((p) => this.permissionSet().has(p));
  }

  login(email: string, password: string): Observable<UserProfile> {
    return this.http.post<AuthResponse>('/api/v1/auth/login', { email, password }).pipe(
      tap((res) => this.setSession(res)),
      map((res) => res.user),
    );
  }

  /** Un solo refresh alla volta: le richieste concorrenti che ricevono 401 condividono lo stesso. */
  refresh(): Observable<string> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.http.post<AuthResponse>('/api/v1/auth/refresh', {}).pipe(
        tap((res) => this.setSession(res)),
        map((res) => res.accessToken),
        finalize(() => (this.refreshInFlight$ = null)),
        shareReplay(1),
      );
    }
    return this.refreshInFlight$;
  }

  /** Chiamato all'avvio: se esiste una sessione valida (cookie di refresh) la ripristina in silenzio. */
  restoreSession(): Promise<void> {
    return new Promise((resolve) => {
      this.refresh().pipe(catchError(() => of(null))).subscribe({ complete: () => resolve() });
    });
  }

  logout(): Observable<void> {
    return this.http.post<void>('/api/v1/auth/logout', {}).pipe(
      catchError(() => of(void 0)),
      finalize(() => this.clearSession()),
    );
  }

  /** Ricarica profilo e permessi dal server (es. dopo una modifica dei ruoli). */
  reloadProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>('/api/v1/me').pipe(
      tap((u) => this._user.set(u)),
      catchError((e) => throwError(() => e)),
    );
  }

  clearSession(): void {
    this._token.set(null);
    this._user.set(null);
  }

  private setSession(res: AuthResponse): void {
    this._token.set(res.accessToken);
    this._user.set(res.user);
  }
}
